import { useCallback, useEffect, useState } from 'react';
import { Button, Textarea } from '@/backoffice/components/ui';
import { fetchOrderMessages, sendOrderMessage } from '@/domains/api/messaging.api';
import { getEcho, reverbEnabled } from '@/utils/echo';
import { getSession } from '@/domains/auth/services/auth.service';
import type { ApiOrderMessage } from '@/domains/api/types';
import { formatRelativeTime } from '@/backoffice/utils';
import { Loader2, Send, Wifi } from 'lucide-react';

interface OrderChatPanelProps {
  orderId: number;
  pollIntervalMs?: number;
}

export function OrderChatPanel({ orderId, pollIntervalMs = 15000 }: OrderChatPanelProps) {
  const [messages, setMessages] = useState<ApiOrderMessage[]>([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [live, setLive] = useState(reverbEnabled());

  const load = useCallback(async () => {
    try {
      const list = await fetchOrderMessages(orderId);
      setMessages(list);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    setLoading(true);
    load();

    const token = getSession()?.token ?? null;
    const echo = getEcho(token);

    if (echo) {
      const channel = echo.private(`order.${orderId}`);
      channel.listen('.message.sent', (payload: { message: ApiOrderMessage }) => {
        setMessages((prev) => {
          if (prev.some((m) => m.id === payload.message.id)) return prev;
          return [payload.message, ...prev];
        });
      });
      setLive(true);

      return () => {
        channel.stopListening('.message.sent');
        echo.leave(`order.${orderId}`);
      };
    }

    const interval = setInterval(load, pollIntervalMs);
    setLive(false);
    return () => clearInterval(interval);
  }, [orderId, load, pollIntervalMs]);

  const handleSend = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await sendOrderMessage(orderId, body.trim());
      setBody('');
      if (!live) await load();
    } finally {
      setSending(false);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className="flex justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des messages…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-80">
      {live && (
        <p className="text-xs text-emerald-600 flex items-center gap-1 mb-2">
          <Wifi className="w-3.5 h-3.5" /> Temps réel activé
        </p>
      )}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Aucun message. Démarrez la conversation.</p>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                msg.is_mine ? 'ml-auto bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'
              }`}
            >
              {!msg.is_mine && <p className="text-xs font-medium opacity-70 mb-1">{msg.sender_name}</p>}
              <p>{msg.body}</p>
              <p className={`text-[10px] mt-1 ${msg.is_mine ? 'text-indigo-100' : 'text-gray-400'}`}>
                {msg.created_at ? formatRelativeTime(msg.created_at) : ''}
              </p>
            </div>
          ))
        )}
      </div>
      <div className="mt-3 flex gap-2 items-end border-t pt-3">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Votre message…"
          rows={2}
          className="flex-1"
        />
        <Button leftIcon={<Send className="w-4 h-4" />} onClick={handleSend} isLoading={sending} disabled={!body.trim()}>
          Envoyer
        </Button>
      </div>
    </div>
  );
}
